import { useEffect, useState } from 'react'

/**
 * Reveal `target` progressively, advancing `charsPerTick` characters every
 * ~16ms. Decouples the visible-text update rate from how fast chunks arrive
 * over the wire, so a bursty LLM stream still reads as smooth typing.
 *
 * Pass `enabled: false` to bypass the animation — the displayed text snaps
 * to `target` instantly. Used for cache hits, where we want the same
 * surface to feel like a re-read rather than a fresh generation.
 *
 * If `target` shrinks or diverges (e.g. user changed the platform filter and
 * the stream restarted), the displayed text resets to "" and the reveal
 * starts over.
 */
export function useTypewriter(target: string, opts?: { enabled?: boolean; charsPerTick?: number }) {
  const enabled = opts?.enabled ?? true
  const charsPerTick = opts?.charsPerTick ?? 3
  const [displayed, setDisplayed] = useState('')

  useEffect(() => {
    if (!enabled) {
      // Snap to full text — no animation.
      if (displayed !== target) setDisplayed(target)
      return
    }

    if (displayed === target) return

    // Stream restarted or text changed in a way that breaks the prefix
    // relationship — snap back to empty and let the reveal catch up.
    if (target.length < displayed.length || !target.startsWith(displayed)) {
      setDisplayed('')
      return
    }

    const id = window.setTimeout(() => {
      setDisplayed(target.slice(0, Math.min(target.length, displayed.length + charsPerTick)))
    }, 16)
    return () => window.clearTimeout(id)
  }, [target, displayed, enabled, charsPerTick])

  return displayed
}
